// fields is a container

import { Category } from '@modules/common/types';
import dayjs from 'dayjs';
import { Source } from 'generated/graphql';
import { ReasonType } from 'v2/core/entities/labels';
import { UserRole, UserType } from 'v2/core/entities/user';
import { UUID } from 'v2/core/entities/uuid';
import { beautifyString } from 'v2/utils/strings';

// of various fields, metadata about them, and their possible values

export type SourceName =
  | 'FRESHCHAT'
  | 'INTERCOM'
  | 'APPSTORE'
  | 'PLAYSTORE'
  | 'TWITTER'
  | 'SLACK'
  | 'LIVECHAT'
  | 'ENTERPRET'
  | 'FILE_UPLOAD'
  | 'KUSTOMER'
  | 'NPS'
  | 'COMMON'
  | 'DISCOURSE'
  | 'ZENDESK_CHAT'
  | 'ZENDESK_SUPPORT'
  | 'DELIGHTED'
  | 'STELLA_CONNECT'
  | 'SURVEY_MONKEY'
  | 'ZOHO_DESK'
  | 'FRONT'
  | 'G2'
  | 'FRESHDESK'
  | 'TYPEFORM'
  | 'GONG'
  | 'EMAIL'
  | 'SALESFORCE'
  | 'DISCORD'
  | 'SNOWFLAKE'
  | 'REDDIT'
  | 'GRAIN'
  | 'FACEBOOK_PAGES'
  | 'QUALTRICS'
  | 'AMAZON_CONNECT';

export interface SourceNameWithNamespace {
  name: SourceName;
  namespace?: string;
}

export interface SourceField {
  values: SourceNameWithNamespace[];
  displayName: 'Source';
}

export type LanguageFieldValue = {
  format: 'iso6393';
  name: string;
  id: string;
};
export interface LanguageField {
  values: LanguageFieldValue[];
  displayName: 'Language';
}

export interface IDField {
  values: string[];
  displayName: 'ID';
}

export enum SentimentName {
  POSITIVE = 'Positive',
  NEUTRAL = 'Neutral',
  NEGATIVE = 'Negative',
}

export enum SentimentType {
  Positive = 'POSITIVE',
  Negative = 'NEGATIVE',
  Neutral = 'NEUTRAL',
}

export const SentimentOptions = [
  SentimentType.Positive,
  SentimentType.Negative,
  SentimentType.Neutral,
];

export enum ViewUnit {
  RECORDS = '#',
  PERCENTAGE = '%',
}

export interface EntitySentimentField {
  values: SentimentName[];
  displayName: 'Entity Sentiment';
}

export enum EntitySentimentSortByType {
  POSITIVE = 'Positive',
  NEUTRAL = 'Neutral',
  NEGATIVE = 'Negative',
  NAME = 'Name',
  VALUE = 'Value',
}

export enum BreakdownSortByType {
  NAME = 'Name',
  VALUE = 'Value',
}

export enum TimeseriesSortByType {
  NAME = 'Name',
  TOTAL = 'Total',
}

export interface ReviewSentimentFieldValue {
  name: SentimentName;
  id: UUID;
}

export interface ReviewSentimentField {
  values: ReviewSentimentFieldValue[];
  displayName: 'User Sentiment';
}

export interface TaxonomyFieldInstance {
  id: UUID; // unique ID in of the node in the taxonomy
  treeID: UUID; // unique ID of the node in taxonomy tree view
  name: string;
  description: string;
  // This should not be optional
  // some gap somewhere
  createdAt?: dayjs.Dayjs;
  subTreeLabelIDs?: UUID[];
}

export interface EntityFieldInstance extends TaxonomyFieldInstance {
  metadata?: {
    phrases?: string[];
  };
}

export interface ReasonFieldInstance extends TaxonomyFieldInstance {
  metadata: {
    sentiment?: SentimentName;
    createdBy?: {
      id: UUID;
      email: string;
      firstName?: string;
      lastName?: string;
      role?: UserRole;
      type: UserType;
    };
    representativeSentences?: string[];
    type?: ReasonType;
    category?: Category;
  };
}

export interface ThemeField {
  values: TaxonomyFieldInstance[];
  displayName: 'Theme';
}

export interface ReasonField {
  values: ReasonFieldInstance[];
  displayName: 'Reason';
}

export interface EntityField {
  values: EntityFieldInstance[];
  displayName: 'Entity';
}

export type MetadataFieldValuesType = number[] | string[] | boolean[];
export type MetadataFieldValueType = 'number' | 'string' | 'boolean';
export interface MetadataFieldValue {
  querKey: string;
  cardinality: number;
  type: MetadataFieldValueType;
  // TODO: improve typing of type and values
  values: MetadataFieldValuesType;
  source: SourceNameWithNamespace;
  name: string;
  valueToNameMap?: Record<string, string>;
  description: string;
  originalDisplayText: string;
  isHidden: boolean;
  isImportant: boolean;
  isFilterable: boolean;
  isGroupable: boolean;
}

export interface MetadataField {
  values: MetadataFieldValue[];
  hiddenValues: MetadataFieldValue[];
  displayName: 'Metadata';
}

export interface CategoryField {
  values: Category;
  displayName: 'Category';
}

export interface FeedbackRecordField {
  displayName: 'Feedback Records';
  values: unknown[];
}

export interface FieldContainer {
  Entity: EntityField;
  Theme: ThemeField;
  Reason: ReasonField;
  EntitySentiment?: EntitySentimentField;
  ReviewSentiment?: ReviewSentimentField;
  Source: SourceField;
  Language: LanguageField;
  Metadata: MetadataField;
  Category: CategoryField;
  ID: IDField;
  FeedbackRecords: FeedbackRecordField;
}
export type FieldName = keyof FieldContainer & string;
export type TaxonomyFields = Pick<
  FieldContainer,
  'Theme' | 'Entity' | 'Reason'
>;
export type TaxonomyFieldName = keyof TaxonomyFields;
export type TaxonomyGroupByKeys = Pick<
  FieldContainer,
  'Theme' | 'Entity' | 'Reason' | 'EntitySentiment'
>;
export type TaxonomyGroupByKey = keyof TaxonomyGroupByKeys;
export type SimpleFields = Omit<
  FieldContainer,
  'Theme' | 'Entity' | 'Reason' | 'Metadata'
>;
export type SimpleFieldName = keyof SimpleFields;

export enum FilterKeyNames {
  ThemeFilter = 'labelHierarchyFilter',
  EntityFilter = 'entityFilter',
  ReasonFilter = 'reasonFilter',
  ReviewSentimentFilter = 'reviewSentimentFilter',
  LanguageFilter = 'languageFilter',
  SourceFilter = 'sourceFilter',
  MetadataFilter = 'metadataFilter',
  HybridSearchFilter = 'hybridSearchFilter',
  CategoryFilter = 'categoryFilter',
}

export enum FieldNames {
  Theme = 'Theme',
  Entity = 'Entity',
  Reason = 'Reason',
  EntitySentiment = 'EntitySentiment',
  ReviewSentiment = 'ReviewSentiment',
  Language = 'Language',
  Source = 'Source',
  Metadata = 'Metadata',
  Category = 'Category',
  ID = 'ID',
  FeedbackRecords = 'FeedbackRecords',
}

export const isTaxonomyField = (fieldName: FieldName): boolean => {
  switch (fieldName) {
    case 'Entity':
    case 'Reason':
    case 'Theme':
      return true;
    default:
      return false;
  }
};

export const isStringOfTypeSourceName = (str: string): str is SourceName => {
  return Boolean(
    Object.values(Source).find((source) => source.toString() === str),
  );
};

export const sourceSerializer = (source: SourceName | string = '') =>
  beautifyString(source);

//TODO fix return type from any.
export const sourceDeserializer = (source: string) => {
  try {
    return JSON.parse(source);
  } catch (e) {
    return {
      name: source,
    };
  }
};
